// apps/web/src/components/HealthIndicator.js
import React, { useState, useEffect } from 'react';
import './HealthIndicator.css';

/**
 * HealthIndicator - Shows real-time health status of frontend and backend
 *
 * Displays:
 * - Frontend status (always online if component renders)
 * - Backend API status (checks /health endpoint)
 * - Database status (from API health check)
 * - Last check timestamp
 *
 * Usage:
 * <HealthIndicator position="bottom-right" />
 */
export default function HealthIndicator({ position = 'bottom-right', expanded = false }) {
  const [apiStatus, setApiStatus] = useState('checking');  // 'online', 'offline', 'checking', 'degraded'
  const [dbStatus, setDbStatus] = useState('checking');
  const [lastCheck, setLastCheck] = useState(null);
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [responseTime, setResponseTime] = useState(null);

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  async function checkHealth() {
    const startTime = performance.now();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      const healthUrl = apiUrl.replace('/api/v1', '/health');

      const response = await fetch(healthUrl, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      clearTimeout(timeoutId);
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);
      setResponseTime(latency);

      if (response.ok) {
        const data = await response.json();
        setApiStatus(latency > 1000 ? 'degraded' : 'online');
        setDbStatus(data.database === false ? 'offline' : 'online');
      } else {
        setApiStatus('degraded');
        setDbStatus('unknown');
      }

      setLastCheck(new Date());
    } catch (err) {
      console.error('[HealthIndicator] Health check failed:', err);
      setApiStatus('offline');
      setDbStatus('unknown');
      setResponseTime(null);
      setLastCheck(new Date());
    }
  }

  const frontendStatus = 'online'; // If this component renders, frontend is online

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#10b981'; // Green
      case 'degraded': return '#f59e0b'; // Orange
      case 'offline': return '#ef4444'; // Red
      case 'checking': return '#6b7280'; // Gray
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'degraded': return 'Degraded';
      case 'offline': return 'Offline';
      case 'checking': return 'Checking...';
      case 'unknown': return 'Unknown';
      default: return 'Unknown';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return '●';
      case 'degraded': return '◐';
      case 'offline': return '○';
      case 'checking': return '◔';
      default: return '○';
    }
  };

  return (
    <div className={`HealthIndicator ${position} ${isExpanded ? 'expanded' : ''}`}>
      {/* Compact view */}
      <div
        className="HealthIndicatorCompact"
        onClick={() => setIsExpanded(!isExpanded)}
        title="Click to expand health status"
      >
        <span
          className="HealthIndicatorDot"
          style={{ color: getStatusColor(apiStatus) }}
        >
          {getStatusIcon(apiStatus)}
        </span>
        <span className="HealthIndicatorLabel">System Status</span>
      </div>

      {/* Expanded view */}
      {isExpanded && (
        <div className="HealthIndicatorExpanded">
          <div className="HealthIndicatorHeader">
            <span className="HealthIndicatorTitle">System Health</span>
            <button
              className="HealthIndicatorClose"
              onClick={() => setIsExpanded(false)}
              title="Collapse"
            >
              ✕
            </button>
          </div>

          <div className="HealthIndicatorBody">
            {/* Frontend status */}
            <div className="HealthIndicatorRow">
              <span className="HealthIndicatorRowIcon" style={{ color: getStatusColor(frontendStatus) }}>
                {getStatusIcon(frontendStatus)}
              </span>
              <span className="HealthIndicatorRowLabel">Frontend</span>
              <span className="HealthIndicatorRowStatus" style={{ color: getStatusColor(frontendStatus) }}>
                {getStatusText(frontendStatus)}
              </span>
            </div>

            {/* Backend API status */}
            <div className="HealthIndicatorRow">
              <span className="HealthIndicatorRowIcon" style={{ color: getStatusColor(apiStatus) }}>
                {getStatusIcon(apiStatus)}
              </span>
              <span className="HealthIndicatorRowLabel">Backend API</span>
              <span className="HealthIndicatorRowStatus" style={{ color: getStatusColor(apiStatus) }}>
                {getStatusText(apiStatus)}
                {responseTime !== null && apiStatus === 'online' && (
                  <span className="HealthIndicatorLatency"> ({responseTime}ms)</span>
                )}
                {responseTime !== null && apiStatus === 'degraded' && (
                  <span className="HealthIndicatorLatency"> (Slow: {responseTime}ms)</span>
                )}
              </span>
            </div>

            {/* Database status */}
            <div className="HealthIndicatorRow">
              <span className="HealthIndicatorRowIcon" style={{ color: getStatusColor(dbStatus) }}>
                {getStatusIcon(dbStatus)}
              </span>
              <span className="HealthIndicatorRowLabel">Database</span>
              <span className="HealthIndicatorRowStatus" style={{ color: getStatusColor(dbStatus) }}>
                {getStatusText(dbStatus)}
              </span>
            </div>

            {/* Last check timestamp */}
            {lastCheck && (
              <div className="HealthIndicatorFooter">
                Last checked: {lastCheck.toLocaleTimeString()}
              </div>
            )}

            {/* Manual refresh button */}
            <button
              className="HealthIndicatorRefresh"
              onClick={checkHealth}
              disabled={apiStatus === 'checking'}
            >
              {apiStatus === 'checking' ? 'Checking...' : 'Refresh'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
